import { useEffect, useState, useCallback, useMemo } from "react";
import { CreditCard, RefreshCw, Search } from "lucide-react";
import { LoadMoreBar, PAGE_SIZE as SERVER_PAGE, totalFrom } from "@/components/LoadMoreBar";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const STATUS_OPTS = [
  { v: "all", label: "All" },
  { v: "paid", label: "Paid" },
  { v: "pending", label: "Pending" },
];

export default function PlatformPayments() {
  const [payments, setPayments] = useState(null);
  const [total, setTotal] = useState(0);
  const [moreLoading, setMoreLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/platform/payments", { params: { limit: SERVER_PAGE } });
      setPayments(res.data); setTotal(totalFrom(res));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    if (!payments) return [];
    const ql = q.trim().toLowerCase();
    return payments.transactions.filter((t) =>
      (statusFilter === "all" ||
        (statusFilter === "paid" ? t.payment_status === "paid" : t.payment_status !== "paid")) &&
      (!ql || (t.target_name || "").toLowerCase().includes(ql) || (t.user_email || "").toLowerCase().includes(ql))
    );
  }, [payments, q, statusFilter]);

  return (
    <main className="px-6 md:px-10 py-8" data-testid="platform-payments-page">
      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <CreditCard className="h-6 w-6 text-primary" /> Stripe payments
          </h1>
          <div className="text-xs text-slate-500">Every subscription checkout collected through Stripe.</div>
        </div>
        <button
          onClick={load}
          data-testid="platform-refresh-btn"
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 transition"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </header>

      <section className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="platform-payments">
        <div className="flex flex-col gap-3 px-5 py-4 border-b border-slate-200 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search account or payer email…"
                data-testid="platform-payment-search"
                className="w-full h-9 rounded-lg border border-slate-200 bg-white pl-8 pr-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
              />
            </div>
            <div className="flex items-center gap-2" data-testid="platform-payment-status-filter">
              {STATUS_OPTS.map((opt) => (
                <button
                  key={opt.v}
                  onClick={() => setStatusFilter(opt.v)}
                  data-testid={`platform-payment-filter-${opt.v}`}
                  className={`inline-flex items-center rounded-full px-3 py-1.5 text-xs font-medium transition ${
                    statusFilter === opt.v ? "bg-slate-900 text-white" : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          {payments && (
            <div className="flex items-center gap-4 text-xs" data-testid="platform-payments-summary">
              <span className="font-semibold text-emerald-700">${payments.collected_usd.toFixed(2)} collected</span>
              <span className="text-slate-500">{payments.paid_count} paid</span>
              <span className="text-slate-400">{payments.pending_count} pending/abandoned</span>
            </div>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="platform-payments-table">
            <thead className="sticky top-0">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <th className="px-5 py-3">Date</th>
                <th className="px-5 py-3">Account</th>
                <th className="px-5 py-3">Paid by</th>
                <th className="px-5 py-3 text-right">Amount</th>
                <th className="px-5 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && !loading && (
                <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-500 text-sm">No payments match — they appear here the moment a customer checks out.</td></tr>
              )}
              {visible.map((t) => (
                <tr key={t.id} className="border-t border-slate-100" data-testid={`platform-payment-${t.id}`}>
                  <td className="px-5 py-3 text-slate-700 whitespace-nowrap">{t.created_at ? new Date(t.created_at).toLocaleString() : "—"}</td>
                  <td className="px-5 py-3">
                    <div className="font-medium text-slate-900">{t.target_name}</div>
                    <div className="text-[11px] text-slate-400 uppercase">{t.target_type}</div>
                  </td>
                  <td className="px-5 py-3 text-slate-700">{t.user_email}</td>
                  <td className="px-5 py-3 text-right font-semibold text-slate-900">${t.amount.toFixed(2)}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${
                      t.payment_status === "paid" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
                    }`}>
                      {t.payment_status === "paid" ? "Paid" : t.payment_status || "Pending"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <LoadMoreBar shown={payments?.length || 0} total={total} loading={moreLoading} testid="payments-load-more" onMore={async () => {
          setMoreLoading(true);
          try { const res = await api.get("/platform/payments", { params: { limit: SERVER_PAGE, offset: payments.length } }); setPayments((p) => [...p, ...res.data]); setTotal(totalFrom(res)); }
          finally { setMoreLoading(false); }
        }} />
      </section>
    </main>
  );
}
